/**
 * Matching Service - Handles all matching-related operations
 * Phase 5 Part 1: Intelligent Matching System
 * Provides methods for fetching matches, match details, history, and explanations
 */

import { matchingAPI } from './api';
import { supabase } from '../lib/supabase';

// Match quality labels (adjusted for weighted scoring with Activity=0)
export const MATCH_QUALITY_LABELS = {
  excellent: { label: 'Excellent', color: 'green', minScore: 65 },
  strong: { label: 'Strong', color: 'blue', minScore: 55 },
  moderate: { label: 'Moderate', color: 'yellow', minScore: 40 },
  weak: { label: 'Weak', color: 'orange', minScore: 25 },
  poor: { label: 'Poor', color: 'red', minScore: 0 }
};

// Recommended action labels
export const RECOMMENDED_ACTIONS = {
  reach_out: { label: 'Reach Out', priority: 'high', icon: 'send' },
  monitor: { label: 'Monitor', priority: 'medium', icon: 'eye' },
  wait: { label: 'Wait', priority: 'low', icon: 'clock' },
  low_priority: { label: 'Low Priority', priority: 'minimal', icon: 'archive' }
};

// Timing labels
export const TIMING_LABELS = {
  immediate: 'Act Now',
  this_week: 'This Week',
  this_month: 'This Month',
  not_urgent: 'When Available'
};

// Score component weights (for display)
export const SCORE_WEIGHTS = {
  therapeutic_areas: 0.30,
  tech_platform: 0.12,
  stage_fit: 0.12,
  buyer_need: 0.20,
  market_opportunity: 0.12,
  activity: 0.14
};

const matchingService = {
  /**
   * Get matches for a seller with optional filters
   * Uses backend API which queries match_scores table
   * @param {string} sellerId - Seller ID
   * @param {Object} options - Query options
   * @returns {Promise<{matches: Array, count: number}>}
   */
  async getSellerMatches(sellerId, {
    limit = 50,
    offset = 0,
    minScore = 0,
    quality = null,
    sortBy = 'score',
    sortOrder = 'desc'
  } = {}) {
    try {
      // Use backend API to get recommendations
      const response = await matchingAPI.getRecommendations(sellerId);

      if (!response.data?.success) {
        throw new Error(response.data?.message || 'Failed to fetch matches');
      }

      const recommendations = response.data?.data?.recommendations || [];

      // Filter by minScore
      let filtered = recommendations.filter(r => (r.score || 0) >= minScore);

      // Filter by quality if specified (adjusted thresholds for weighted scoring)
      if (quality) {
        filtered = filtered.filter(r => {
          const score = r.score || 0;
          if (quality === 'excellent') return score >= 65;
          if (quality === 'strong') return score >= 55 && score < 65;
          if (quality === 'moderate') return score >= 40 && score < 55;
          if (quality === 'weak') return score >= 25 && score < 40;
          return true;
        });
      }

      // Sort
      filtered.sort((a, b) => {
        const aVal = sortBy === 'score' ? (a.score || 0) : new Date(a.calculated_at || 0).getTime();
        const bVal = sortBy === 'score' ? (b.score || 0) : new Date(b.calculated_at || 0).getTime();
        return sortOrder === 'desc' ? bVal - aVal : aVal - bVal;
      });

      // Paginate
      const paginated = filtered.slice(offset, offset + limit);

      // Transform data for UI
      const matches = paginated.map(match => this.transformMatch({
        id: `${sellerId}___${match.buyer_id}`,
        seller_id: sellerId,
        buyer_id: match.buyer_id,
        final_score: match.score,
        match_explanation: match.reasons,
        calculated_at: match.calculated_at,
        // Include component scores from backend
        therapeutic_match_score: match.therapeutic_match_score,
        platform_match_score: match.platform_match_score,
        buyer_need_score: match.buyer_need_score,
        stage_match_score: match.stage_match_score,
        market_opportunity_score: match.market_opportunity_score,
        activity_score: match.activity_score,
        matching_therapeutic_areas: match.matching_therapeutic_areas,
        matching_tech_platforms: match.matching_tech_platforms,
        match_quality: match.match_quality,
        // Recommended action and timing from backend
        recommended_action: match.recommended_action,
        recommended_timing: match.recommended_timing,
        // Signal data
        signal_multiplier: match.signal_multiplier,
        urgency_factor: match.urgency_factor,
        signal_boost_reasons: match.signal_boost_reasons,
        // Why This Match reasons from backend
        match_reasons: match.match_reasons,
        relevant_signals: match.relevant_signals,
        signal_count: match.signal_summary?.totalSignals || 0,
        profile_view_count: match.signal_summary?.profile_views || 0,
        buyers: {
          id: match.buyer_id,
          company_name: match.company_name,
          headquarters: match.headquarters,
          therapeutic_areas: match.therapeutic_areas,
          tech_platforms: match.tech_platforms,
          bd_focus: match.bd_focus,
        }
      }));

      return { matches, count: filtered.length };
    } catch (error) {
      console.error('Error fetching seller matches:', error);
      throw error;
    }
  },

  /**
   * Get matches for a buyer with optional filters
   * @param {string} buyerId - Buyer ID
   * @param {Object} options - Query options
   * @returns {Promise<{matches: Array, count: number}>}
   */
  async getBuyerMatches(buyerId, {
    limit = 50,
    offset = 0,
    minScore = 0,
    quality = null,
    sortBy = 'score',
    sortOrder = 'desc'
  } = {}) {
    try {
      // Use match_scores table (same as backend) for consistency
      let query = supabase
        .from('match_scores')
        .select(`
          seller_id,
          buyer_id,
          score,
          reasons,
          calculated_at,
          sellers:seller_id(
            id,
            company_name,
            headquarters,
            therapeutic_areas,
            stage,
            tech_platforms
          )
        `, { count: 'exact' })
        .eq('buyer_id', buyerId)
        .gte('score', minScore);

      // Note: quality filter removed as match_quality column may not exist

      // Apply sorting - use columns from match_scores table
      const sortColumn = sortBy === 'score' ? 'score' :
                        sortBy === 'date' ? 'calculated_at' : 'score';
      query = query.order(sortColumn, { ascending: sortOrder === 'asc' });

      // Apply pagination
      query = query.range(offset, offset + limit - 1);

      const { data, error, count } = await query;

      if (error) throw error;

      // Transform data for easier use
      const matches = (data || []).map(match => this.transformMatch(match, 'buyer'));

      return { matches, count };
    } catch (error) {
      console.error('Error fetching buyer matches:', error);
      throw error;
    }
  },

  /**
   * Get top matches for a seller using the view
   * @param {string} sellerId - Seller ID
   * @param {number} limit - Max matches to return
   * @returns {Promise<Array>}
   */
  async getTopMatches(sellerId, limit = 10) {
    try {
      const { data, error } = await supabase
        .from('v_seller_top_matches')
        .select('*')
        .eq('seller_id', sellerId)
        .lte('rank', limit)
        .order('rank', { ascending: true });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching top matches:', error);
      throw error;
    }
  },

  /**
   * Get detailed match information
   * Uses backend API to ensure consistency with match list
   * @param {string} sellerId - Seller ID
   * @param {string} buyerId - Buyer ID
   * @returns {Promise<Object>}
   */
  async getMatchDetails(sellerId, buyerId) {
    try {
      // Use backend API to ensure data consistency
      const response = await matchingAPI.getScore(sellerId, buyerId);

      if (!response.data?.success) {
        throw new Error(response.data?.message || 'Failed to fetch match details');
      }

      const matchData = response.data.data;

      // Transform the data for UI consistency
      return this.transformMatch({
        id: `${sellerId}___${buyerId}`,
        seller_id: sellerId,
        buyer_id: buyerId,
        final_score: matchData.score || matchData.final_score,
        match_explanation: matchData.reasons,
        calculated_at: matchData.calculated_at,
        // Component scores from backend
        therapeutic_match_score: matchData.therapeutic_match_score,
        platform_match_score: matchData.platform_match_score,
        buyer_need_score: matchData.buyer_need_score,
        stage_match_score: matchData.stage_match_score,
        market_opportunity_score: matchData.market_opportunity_score,
        activity_score: matchData.activity_score,
        matching_therapeutic_areas: matchData.matching_therapeutic_areas,
        matching_tech_platforms: matchData.matching_tech_platforms,
        match_quality: matchData.match_quality,
        match_tier: matchData.match_tier,
        // Recommended action and timing from backend
        recommended_action: matchData.recommended_action,
        recommended_timing: matchData.recommended_timing,
        // Signal data
        signal_multiplier: matchData.signal_multiplier,
        urgency_factor: matchData.urgency_factor,
        signal_boost_reasons: matchData.signal_boost_reasons,
        signal_summary: matchData.signal_summary,
        // Why This Match reasons from backend
        match_reasons: matchData.match_reasons,
        relevant_signals: matchData.relevant_signals,
        signal_count: matchData.signal_summary?.totalSignals || 0,
        profile_view_count: matchData.signal_summary?.profile_views || 0,
        buyers: matchData.buyers
      });
    } catch (error) {
      console.error('Error fetching match details:', error);
      throw error;
    }
  },

  /**
   * Get match history for tracking score changes over time
   * @param {string} sellerId - Seller ID
   * @param {string} buyerId - Buyer ID
   * @param {number} limit - Max history entries to return
   * @returns {Promise<Array>}
   */
  async getMatchHistory(sellerId, buyerId, limit = 30) {
    try {
      const { data, error } = await supabase
        .from('match_history')
        .select('*')
        .eq('seller_id', sellerId)
        .eq('buyer_id', buyerId)
        .order('recorded_at', { ascending: false })
        .limit(limit);

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching match history:', error);
      throw error;
    }
  },

  /**
   * Get match explanation using database function
   * @param {string} sellerId - Seller ID
   * @param {string} buyerId - Buyer ID
   * @returns {Promise<Object>}
   */
  async getMatchExplanation(sellerId, buyerId) {
    try {
      const { data, error } = await supabase.rpc('get_match_explanation', {
        p_seller_id: sellerId,
        p_buyer_id: buyerId
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error getting match explanation:', error);
      throw error;
    }
  },

  /**
   * Trigger recalculation of a specific match
   * @param {string} sellerId - Seller ID
   * @param {string} buyerId - Buyer ID
   * @param {string} triggerEvent - Event that triggered recalculation
   * @returns {Promise<string>} Match ID
   */
  async recalculateMatch(sellerId, buyerId, triggerEvent = 'manual') {
    try {
      const { data, error } = await supabase.rpc('upsert_match', {
        p_buyer_id: buyerId,
        p_seller_id: sellerId,
        p_trigger_event: triggerEvent
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error recalculating match:', error);
      throw error;
    }
  },

  /**
   * Batch recalculate all matches for a seller
   * Uses backend API to calculate matches
   * @param {string} sellerId - Seller ID
   * @returns {Promise<number>} Number of matches recalculated
   */
  async batchRecalculateSellerMatches(sellerId) {
    try {
      // Use backend API to calculate matches
      const response = await matchingAPI.calculate();

      if (!response.data?.success) {
        throw new Error(response.data?.message || 'Failed to calculate matches');
      }

      return response.data?.data?.total_matches || 0;
    } catch (error) {
      console.error('Error batch recalculating matches:', error);
      throw error;
    }
  },

  /**
   * Get match score trend for a specific match
   * @param {string} sellerId - Seller ID
   * @param {string} buyerId - Buyer ID
   * @param {number} days - Number of days to look back
   * @returns {Promise<Object>} Trend data
   */
  async getMatchScoreTrend(sellerId, buyerId, days = 30) {
    try {
      const { data, error } = await supabase.rpc('get_match_score_trend', {
        p_seller_id: sellerId,
        p_buyer_id: buyerId,
        p_days: days
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error getting match score trend:', error);
      throw error;
    }
  },

  /**
   * Get comparable deals for context
   * @param {string} sellerId - Seller ID
   * @param {number} limit - Max deals to return
   * @returns {Promise<Array>}
   */
  async getComparableDeals(sellerId, limit = 10) {
    try {
      const { data, error } = await supabase.rpc('get_comparable_deals', {
        p_seller_id: sellerId,
        p_limit: limit
      });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error getting comparable deals:', error);
      throw error;
    }
  },

  /**
   * Get hot matches (high score + high activity)
   * @param {string} sellerId - Seller ID
   * @param {number} limit - Max matches to return
   * @returns {Promise<Array>}
   */
  async getHotMatches(sellerId, limit = 10) {
    try {
      const { data, error } = await supabase
        .from('v_hot_matches')
        .select('*')
        .eq('seller_id', sellerId)
        .limit(limit);

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching hot matches:', error);
      throw error;
    }
  },

  /**
   * Get match score trends view
   * @param {string} sellerId - Seller ID
   * @returns {Promise<Array>}
   */
  async getMatchTrends(sellerId) {
    try {
      const { data, error } = await supabase
        .from('v_match_score_trends')
        .select('*')
        .eq('seller_id', sellerId)
        .order('score_change', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching match trends:', error);
      throw error;
    }
  },

  /**
   * Get matching statistics for a seller
   * Uses backend API
   * @param {string} sellerId - Seller ID
   * @returns {Promise<Object>}
   */
  async getMatchingStats(sellerId) {
    try {
      // Use backend API to get recommendations and calculate stats
      const response = await matchingAPI.getRecommendations(sellerId);

      if (!response.data?.success) {
        // Return empty stats if no data
        return {
          totalMatches: 0,
          averageScore: 0,
          byQuality: { excellent: 0, strong: 0, moderate: 0, weak: 0, poor: 0 },
          byTrend: { improving: 0, stable: 0, declining: 0 },
          highActivityMatches: 0
        };
      }

      const recommendations = response.data?.data?.recommendations || [];

      // Calculate stats based on score ranges
      // Adjusted thresholds for new weighted scoring (Activity=0 brings average down)
      const stats = {
        totalMatches: recommendations.length,
        averageScore: recommendations.length > 0
          ? Math.round(recommendations.reduce((sum, m) => sum + (m.score || 0), 0) / recommendations.length)
          : 0,
        byQuality: {
          excellent: recommendations.filter(m => (m.score || 0) >= 65).length,
          strong: recommendations.filter(m => (m.score || 0) >= 55 && (m.score || 0) < 65).length,
          moderate: recommendations.filter(m => (m.score || 0) >= 40 && (m.score || 0) < 55).length,
          weak: recommendations.filter(m => (m.score || 0) >= 25 && (m.score || 0) < 40).length,
          poor: recommendations.filter(m => (m.score || 0) < 25).length
        },
        byTrend: {
          improving: 0,
          stable: recommendations.length,
          declining: 0
        },
        highActivityMatches: 0
      };

      return stats;
    } catch (error) {
      console.error('Error fetching matching stats:', error);
      throw error;
    }
  },

  /**
   * Get or create buyer matching criteria
   * @param {string} buyerId - Buyer ID
   * @returns {Promise<Object>}
   */
  async getBuyerCriteria(buyerId) {
    try {
      const { data, error } = await supabase.rpc('get_or_create_matching_criteria', {
        p_buyer_id: buyerId
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error getting buyer criteria:', error);
      throw error;
    }
  },

  /**
   * Update buyer matching criteria
   * @param {string} buyerId - Buyer ID
   * @param {Object} criteria - Criteria to update
   * @returns {Promise<Object>}
   */
  async updateBuyerCriteria(buyerId, criteria) {
    try {
      const { data, error } = await supabase
        .from('matching_criteria')
        .upsert({
          buyer_id: buyerId,
          ...criteria,
          updated_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating buyer criteria:', error);
      throw error;
    }
  },

  /**
   * Subscribe to real-time match score updates
   * @param {string} sellerId - Seller ID
   * @param {Function} callback - Called when match is updated
   * @returns {Object} Subscription object with unsubscribe method
   */
  subscribeToMatchUpdates(sellerId, callback) {
    const subscription = supabase
      .channel(`matches-${sellerId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'match_scores',
          filter: `seller_id=eq.${sellerId}`
        },
        (payload) => {
          callback({
            event: payload.eventType,
            match: this.transformMatch(payload.new)
          });
        }
      )
      .subscribe();

    return {
      unsubscribe: () => subscription.unsubscribe()
    };
  },

  /**
   * Transform raw match data for UI consumption
   * @param {Object} match - Raw match data from database
   * @param {string} perspective - 'seller' or 'buyer' perspective
   * @returns {Object} Transformed match data
   */
  transformMatch(match, perspective = 'seller') {
    if (!match) return null;

    // Derive quality from score if match_quality column doesn't exist
    // Adjusted thresholds for new weighted scoring (Activity=0 brings average down)
    const score = match.final_score || 0;
    const derivedQuality = score >= 65 ? 'excellent' :
                           score >= 55 ? 'strong' :
                           score >= 40 ? 'moderate' :
                           score >= 25 ? 'weak' : 'poor';

    const quality = match.match_quality || derivedQuality;
    const qualityConfig = MATCH_QUALITY_LABELS[quality] || MATCH_QUALITY_LABELS.poor;

    // Derive action from score if recommended_action column doesn't exist
    // Adjusted thresholds for new weighted scoring (Activity=0 brings average down)
    const derivedAction = score >= 55 ? 'reach_out' :
                          score >= 40 ? 'monitor' :
                          score >= 25 ? 'wait' : 'low_priority';
    const action = match.recommended_action || derivedAction;
    const actionConfig = RECOMMENDED_ACTIONS[action] || RECOMMENDED_ACTIONS.low_priority;

    return {
      ...match,
      // Ensure match_quality is set
      match_quality: quality,

      // Quality display
      qualityLabel: qualityConfig.label,
      qualityColor: qualityConfig.color,

      // Action display
      actionLabel: actionConfig.label,
      actionPriority: actionConfig.priority,
      actionIcon: actionConfig.icon,

      // Timing display
      timingLabel: TIMING_LABELS[match.recommended_timing] || 'When Available',

      // Score breakdown for charts
      scoreBreakdown: {
        therapeuticAreas: {
          score: match.therapeutic_match_score || 0,
          weight: SCORE_WEIGHTS.therapeutic_areas,
          label: 'Therapeutic Areas'
        },
        techPlatform: {
          score: match.platform_match_score || 0,
          weight: SCORE_WEIGHTS.tech_platform,
          label: 'Technology Platform'
        },
        stageFit: {
          score: match.stage_match_score || 0,
          weight: SCORE_WEIGHTS.stage_fit,
          label: 'Development Stage'
        },
        buyerNeed: {
          score: match.buyer_need_score || 0,
          weight: SCORE_WEIGHTS.buyer_need,
          label: 'Buyer Need'
        },
        marketOpportunity: {
          score: match.market_opportunity_score || 0,
          weight: SCORE_WEIGHTS.market_opportunity,
          label: 'Market Opportunity'
        },
        activity: {
          score: match.activity_score || 0,
          weight: SCORE_WEIGHTS.activity,
          label: 'Buyer Activity'
        }
      },

      // Trend information (defaults if columns don't exist)
      trendInfo: {
        direction: match.score_trend || 'stable',
        change: match.score_change || 0,
        previousScore: match.previous_score
      },

      // Matching details
      matchingDetails: {
        therapeuticAreas: match.matching_therapeutic_areas || [],
        techPlatforms: match.matching_tech_platforms || [],
        keywords: match.matching_keywords || []
      },

      // Related entity name based on perspective
      companyName: perspective === 'seller'
        ? match.buyers?.company_name
        : match.sellers?.company_name,
      companyTicker: match.buyers?.stock_ticker,

      // Confidence as percentage
      confidencePercent: Math.round((match.confidence || 0) * 100)
    };
  },

  /**
   * Get quality badge class for styling
   * @param {string} quality - Match quality level
   * @returns {string} CSS class name
   */
  getQualityBadgeClass(quality) {
    const classes = {
      excellent: 'bg-green-100 text-green-800 border-green-200',
      strong: 'bg-blue-100 text-blue-800 border-blue-200',
      moderate: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      weak: 'bg-orange-100 text-orange-800 border-orange-200',
      poor: 'bg-red-100 text-red-800 border-red-200'
    };
    return classes[quality] || classes.poor;
  },

  /**
   * Get trend icon based on score trend
   * @param {string} trend - Trend direction
   * @returns {Object} Icon info
   */
  getTrendIcon(trend) {
    const icons = {
      improving: { icon: 'arrow-up', color: 'text-green-500' },
      stable: { icon: 'minus', color: 'text-gray-500' },
      declining: { icon: 'arrow-down', color: 'text-red-500' }
    };
    return icons[trend] || icons.stable;
  },

  // ============================================================================
  // NEW BUYER INTELLIGENCE METHODS
  // ============================================================================

  /**
   * Get comprehensive buyer intelligence scores
   * Includes Financial Capacity, Deal Pattern, Portfolio Gap, and Earnings Call scores
   * @param {string} buyerId - Buyer ID
   * @returns {Promise<Object>} Comprehensive intelligence data
   */
  async getBuyerIntelligenceScores(buyerId) {
    try {
      const response = await matchingAPI.getIntelligence(buyerId);

      if (!response.data?.success) {
        throw new Error(response.data?.message || 'Failed to fetch buyer intelligence');
      }

      return response.data.data;
    } catch (error) {
      console.error('Error fetching buyer intelligence scores:', error);
      throw error;
    }
  },

  /**
   * Get specific intelligence score by type
   * @param {string} buyerId - Buyer ID
   * @param {string} scoreType - financial_capacity | deal_pattern | portfolio_gap | earnings_call
   * @returns {Promise<Object>} Specific score data
   */
  async getIntelligenceScoreByType(buyerId, scoreType) {
    try {
      const response = await matchingAPI.getIntelligenceByType(buyerId, scoreType);

      if (!response.data?.success) {
        throw new Error(response.data?.message || 'Failed to fetch intelligence score');
      }

      return response.data.data;
    } catch (error) {
      console.error(`Error fetching ${scoreType} score:`, error);
      throw error;
    }
  },

  /**
   * Get recommendation level based on composite intelligence score
   * @param {number} score - Composite intelligence score (0-100)
   * @returns {Object} Recommendation info
   */
  getIntelligenceRecommendation(score) {
    if (score >= 70) {
      return {
        level: 'high_priority',
        label: 'High Priority',
        color: 'green',
        description: 'Strong alignment across all dimensions - prioritize engagement'
      };
    } else if (score >= 50) {
      return {
        level: 'good_fit',
        label: 'Good Fit',
        color: 'blue',
        description: 'Solid opportunity - worth pursuing actively'
      };
    } else if (score >= 30) {
      return {
        level: 'moderate',
        label: 'Moderate',
        color: 'yellow',
        description: 'Some alignment - monitor for changes'
      };
    } else {
      return {
        level: 'low_priority',
        label: 'Low Priority',
        color: 'gray',
        description: 'Limited alignment - focus elsewhere'
      };
    }
  },

  /**
   * Format intelligence score for display
   * @param {Object} scoreData - Score data from API
   * @returns {Object} Formatted display data
   */
  formatIntelligenceScore(scoreData) {
    const { score, components, insights } = scoreData;

    return {
      score,
      displayScore: `${score}/100`,
      scoreColor: score >= 70 ? 'green' : score >= 50 ? 'blue' : score >= 30 ? 'yellow' : 'gray',
      components: Object.entries(components || {}).map(([key, value]) => ({
        name: key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()),
        value,
        percentage: Math.round((value / 100) * 100)
      })),
      insights: insights || [],
      hasData: score > 0 || (insights && insights.length > 0)
    };
  }
};

export default matchingService;
